/**
 * AccountPageV2 — redesigned "My Account" page.
 *
 * Layout: PageHeader + edit-canvas grid (form + 340px rail).
 *
 *   Main column:
 *     01 Profile — Name + Email (FieldRow)
 *     02 Change password — Password + Confirmation (FieldRow).
 *        Leave blank to keep the current password (parity with v1).
 *
 *   Right rail:
 *     "Profile" RailCard with a 120px AdminAvatar that doubles as
 *     the photo upload target. Hover reveals a navy translucent
 *     "Change photo" overlay; click opens the file picker; selecting
 *     a file shows it immediately via a blob URL and stages the
 *     File object for the next save.
 *
 *     Below the avatar: live name + email from the form state, then
 *     a divider and a tiny admin_id row.
 *
 * Save plumbing: same updateProfile action v1 uses. On success we
 * clear the password fields so a second save doesn't try to re-set
 * the password.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import SessionHelper from '../../../Helper/SessionHelper';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import { updateProfile } from '../../../../actions/adminsActions';
import { preventEnterFormSubmission } from '../../../../utils/formUtils';
import { PageHeader, Button, cn } from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  RailCard,
  RailCardHead,
} from '../../../redesign/edit';
import AdminAvatar from '../../AdminPage/v2/AdminAvatar';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const CameraIconSvg = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

/* ─── Editable avatar — circular AdminAvatar with click-to-upload
 *  + "Change photo" hover overlay. */

function EditableAvatar({ admin, onChangeFile }) {
  const fileRef = useRef(null);
  const openPicker = () => fileRef.current?.click();

  return (
    <div className="relative group" style={{ width: 120, height: 120 }}>
      <button
        type="button"
        onClick={openPicker}
        aria-label="Change profile photo"
        className="block relative overflow-hidden"
        style={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        <AdminAvatar
          id={admin?.admin_id || 0}
          name={admin?.admin_name}
          image={admin?.admin_image}
          size={120}
        />
        {/* Hover overlay — translucent navy with the camera glyph. */}
        <span
          aria-hidden
          className={cn(
            'absolute inset-0 grid place-items-center text-white',
            'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
          )}
          style={{
            background: 'rgba(44, 62, 80, 0.65)',
            borderRadius: '50%',
            transition: 'opacity 160ms ease',
            gap: 6,
            fontSize: 11,
            fontFamily: 'var(--font-condensed)',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            flexDirection: 'column',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {CameraIconSvg}
          <span>Change photo</span>
        </span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          onChangeFile(e);
          e.target.value = '';
        }}
        style={{ display: 'none' }}
      />
    </div>
  );
}

/* ─── Main ────────────────────────────────────────────────────── */

export default function AccountPageV2() {
  const dispatch = useDispatch();
  const loading = useSelector((s) => s.admins.loading);

  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    const me = SessionHelper.currentUser();
    setAdmin(me);
  }, []);

  const handleChange = (e) => {
    setAdmin((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleChangeFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAdmin((prev) => ({
      ...prev,
      admin_image: URL.createObjectURL(file),
      profile_image: file,
    }));
  };

  const handleSave = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const pw = admin?.admin_password?.trim();
    if (pw && admin.admin_password !== admin.admin_password_confirmation) {
      Swal.fire({
        icon: 'error',
        title: 'Password mismatch',
        text: 'Password and confirmation do not match',
      });
      return;
    }
    dispatch(
      updateProfile(admin, () => {
        // Clear password fields after save so a stray re-submit
        // doesn't try to re-set the password.
        setAdmin((prev) => ({
          ...prev,
          admin_password: '',
          admin_password_confirmation: '',
        }));
        Swal.fire({
          icon: 'success',
          title: 'Profile saved',
          timer: 1200,
          showConfirmButton: false,
        });
      }),
    );
  };

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!admin || !!loading} />
      <PageHeader
        eyebrow="Settings & Admin"
        title="My Account"
        actions={
          <Button
            variant="coral"
            type="submit"
            form="account-form"
            leadingIcon={SaveIconSvg}
            disabled={loading || !admin}
          >
            Save
          </Button>
        }
      />

      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24, padding: '24px 32px 60px' }}
      >
        <form
          id="account-form"
          onSubmit={handleSave}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0 }}
        >
          {/* Browsers helpfully autofill the first password input on
           *  page load; this honeypot eats that attempt. (Same trick
           *  v1 uses.) */}
          <input
            type="text"
            style={{ display: 'none' }}
            autoComplete="user-fake-field"
          />

          <FormSection title="Profile" num="01">
            <FieldRow>
              <FieldBlock label="Name" required htmlFor="f-acc-name" noMargin>
                <TextInput
                  id="f-acc-name"
                  name="admin_name"
                  value={admin?.admin_name ?? ''}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </FieldBlock>
              <FieldBlock
                label="Email"
                required
                hint={
                  admin?.is_federated
                    ? 'Locked — managed by your SSO identity provider'
                    : undefined
                }
                htmlFor="f-acc-email"
                noMargin
              >
                <TextInput
                  id="f-acc-email"
                  name="admin_email"
                  type="email"
                  value={admin?.admin_email ?? ''}
                  onChange={handleChange}
                  required
                  autoComplete="user-profile-email"
                  // Federated emails are owned by the identity
                  // provider (Cognito + upstream IdP). The CMS
                  // updating it locally would just be overwritten
                  // on the next login, so disable the input.
                  disabled={!!admin?.is_federated}
                />
              </FieldBlock>
            </FieldRow>
          </FormSection>

          <FormSection
            title="Change password"
            num="02"
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                Leave blank to keep current
              </span>
            }
          >
            <FieldRow>
              <FieldBlock label="New password" htmlFor="f-acc-password" noMargin>
                <TextInput
                  id="f-acc-password"
                  name="admin_password"
                  type="password"
                  autoComplete="new-password"
                  value={admin?.admin_password ?? ''}
                  onChange={handleChange}
                />
              </FieldBlock>
              <FieldBlock
                label="Confirm new password"
                htmlFor="f-acc-password-confirm"
                noMargin
              >
                <TextInput
                  id="f-acc-password-confirm"
                  name="admin_password_confirmation"
                  type="password"
                  autoComplete="new-password"
                  value={admin?.admin_password_confirmation ?? ''}
                  onChange={handleChange}
                />
              </FieldBlock>
            </FieldRow>
          </FormSection>
        </form>

        {/* Right rail — clickable profile photo + live name/email
         *  from the form state. The avatar IS the upload control;
         *  hover surfaces a "Change photo" overlay. */}
        <aside
          className="edit-rail"
          style={{
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          <RailCard
            head={({ open, toggle }) => (
              <RailCardHead
                eyebrow="Profile"
                open={open}
                onToggle={toggle}
              />
            )}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: 18,
                gap: 14,
              }}
            >
              <EditableAvatar admin={admin} onChangeFile={handleChangeFile} />

              <div style={{ textAlign: 'center', minWidth: 0, width: '100%' }}>
                <div
                  className="font-semibold text-navy truncate"
                  style={{ fontSize: 16, lineHeight: 1.25 }}
                  title={admin?.admin_name || ''}
                >
                  {admin?.admin_name || (
                    <span className="text-text-light italic">No name yet</span>
                  )}
                </div>
                <div
                  className="font-mono text-text-muted truncate"
                  style={{ fontSize: 11.5, marginTop: 4 }}
                  title={admin?.admin_email || ''}
                >
                  {admin?.admin_email || '—'}
                </div>
              </div>

              <div
                className="w-full"
                style={{
                  borderTop: '1px solid #E8E0CF',
                  paddingTop: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  fontSize: 11.5,
                }}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="font-condensed font-bold uppercase text-text-muted"
                    style={{ letterSpacing: '0.08em' }}
                  >
                    admin_id
                  </span>
                  <span className="font-mono text-navy">
                    {admin?.admin_id ?? '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span
                    className="font-condensed font-bold uppercase text-text-muted"
                    style={{ letterSpacing: '0.08em' }}
                  >
                    Roles
                  </span>
                  <span className="font-mono text-navy">
                    {Array.isArray(admin?.roles) ? admin.roles.length : 0}
                  </span>
                </div>
              </div>
            </div>
          </RailCard>
        </aside>
      </div>
    </div>
  );
}
